import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { MultiRadio, CheckboxList } from 'shared/components';
import { TimeLog } from 'features/irregularities/index';
import * as helpers from 'features/irregularities/constants';
import { newline2br } from 'shared/helpers';
import isEmpty from 'lodash/isEmpty';
import { withTranslation } from 'react-i18next';

import './IrregularityDetails.scss';

const mapOptions = (list, checkedList) =>
  list.map((option) => ({
    label: option.name,
    key: option.id,
    checked: !!checkedList.find((checkedElement) => checkedElement.stageId === option.id),
  }));

const resetIrregularity = (irreg, roomId) => {
  const rooms = roomId ? [{ roomId }] : [];

  switch (irreg.scope) {
    case 'examinee':
      return {
        ...irreg,
        irregularityRooms: rooms,
      };
    case 'room':
    case 'test_center':
      return {
        ...irreg,
        irregularityExaminees: [],
        irregularityRooms: rooms,
      };
    default:
      return irreg;
  }
};

const mapInvolved = (type) => helpers.involvedList.filter((element) => type[element.flag]);

class IrregularityDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      irregularity: props.irregularity,
      involvedList: mapInvolved(props.irregularity.irregularityType),
      stagesOptions: [],
      errors: {},
    };

    this.onInvolvementChange = this.onInvolvementChange.bind(this);
    this.onStageChange = this.onStageChange.bind(this);
    this.handleConfirm = this.handleConfirm.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  componentDidMount() {
    this.setState({
      stagesOptions:
        this.props.irregularityStages &&
        mapOptions(this.props.irregularityStages, this.state.irregularity.irregularityStages),
    });
  }

  componentDidUpdate(prevProps) {
    if (
      !isEmpty(this.props.irregularityStages) &&
      prevProps.irregularityStages !== this.props.irregularityStages
    ) {
      this.setState({
        stagesOptions: mapOptions(
          this.props.irregularityStages,
          this.state.irregularity.irregularityStages
        ),
      });
    }
  }

  onStageChange(e) {
    const { stagesOptions } = { ...this.state };
    stagesOptions.forEach((stageOption) => {
      if (+stageOption.key === +e.target.name) {
        stageOption.checked = e.target.checked;
      }
    });

    this.setState({
      stagesOptions,
    });
  }

  onInvolvementChange(e) {
    const { value } = e.target;

    this.setState((prevState) => ({
      irregularity: {
        ...prevState.irregularity,
        scope: value,
      },
    }));
  }

  onChange(value) {
    this.setState((prevState) => ({
      irregularity: {
        ...prevState.irregularity,
        ...value,
      },
    }));
  }

  handleConfirm() {
    let { irregularity } = this.state;
    const hasStages = !!this.state.stagesOptions.find((option) => option.checked);
    const hasInvolvement = !!irregularity.scope.length;

    if (!hasStages || !irregularity.duration || !hasInvolvement || !irregularity.startDateTime) {
      this.setState({
        errors: {
          duration: irregularity.duration ? '' : 'errors.durationRequired',
          stage: hasStages ? '' : 'errors.stageRequired',
          involvement: hasInvolvement ? '' : 'errors.involvementRequired',
          time: irregularity.startDateTime ? '' : 'errors.timeInvalid',
        },
      });
      return;
    }

    if (irregularity.scope !== this.props.irregularity.scope) {
      irregularity = resetIrregularity(irregularity, this.props.roomId);
    }

    irregularity.irregularityStages = this.state.stagesOptions
      .filter((option) => option.checked)
      .map((element) => ({
        stageId: element.key,
      }));

    this.setState({
      irregularity,
    });

    this.props.onContinue(irregularity);
  }

  render() {
    const { irregularity } = this.state;

    return (
      <section className="irregularities-details-container">
        <h4>{irregularity.irregularityType.irregularityCategory.name}</h4>
        <p>{irregularity.irregularityType.name}</p>
        <div className="mt-4">
          <h5 className="mb-2">{this.props.t('irregularities.instructions')}</h5>
          <div
            dangerouslySetInnerHTML={{
              __html: newline2br(irregularity.irregularityType.instructions),
            }}
          />
        </div>
        <TimeLog irregularity={irregularity} onChange={this.onChange} errors={this.state.errors} />
        <CheckboxList
          onChange={this.onStageChange}
          checkboxes={this.state.stagesOptions}
          title={this.props.t('irregularities.stageMessage')}
          errors={this.state.errors.stage}
        />

        <MultiRadio
          radioList={this.state.involvedList}
          checkedValue={this.state.irregularity.scope}
          onChange={this.onInvolvementChange}
          title={this.props.t('irregularities.involvementMessage')}
          errors={this.state.errors.involvement}
        />

        <div className="buttons-container w-100">
          <button className="btn btn-warn w-50" onClick={this.props.onBack}>
            {this.props.t('common.labels.back')}
          </button>
          <button className="btn btn-action w-50" onClick={this.handleConfirm}>
            {' '}
            {this.props.t('common.labels.continue')}
          </button>
        </div>
      </section>
    );
  }
}

IrregularityDetails.propTypes = {
  irregularityStages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  irregularity: PropTypes.shape({
    scope: PropTypes.string,
  }).isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(IrregularityDetails);
